<template>
  <div>
    <serveur-info :erreur="error" />
    <my-notification :icon="'desktop'">
      <template #title>
        {{ $t('compCarbonFormsDevicesMain.title-1') }}
      </template>
      <div class="content">
        <p>
          {{ $t('compCarbonFormsDevicesMain.info-1') }} <b>{{ currentYear }}</b
          >{{ $t('compCarbonFormsDevicesMain.info-2') }}
        </p>
      </div>
    </my-notification>
    <br />
    <help-file-format :module="module" />
    <b-notification :has-icon="true" icon="info" :closable="false">
      <em
        >{{ $t('compCarbonFormsDevicesMain.info-10') }}
        <a
          target="_blank"
          class="link"
          href="https://ecoinfo.cnrs.fr/ecodiag-calcul"
        >
          Ecodiag
        </a>
        {{ $t('compCarbonFormsDevicesMain.info-11') }}
        <a target="_blank" class="link" href="https://ecoinfo.cnrs.fr">
          EcoInfo
        </a>
        {{ $t('compCarbonFormsDevicesMain.info-12') }}.</em
      >
    </b-notification>

    <upload
      v-model="source"
      :module="module"
      :infos="infos"
      :callback="callback"
      :formatDescription="formatDescription"
      :disabled="isSubmitted"
      :singleUpdateDisabled="false"
      :bulkOpsDisabled="!authStore.isAuthenticated"
      :withTags="authStore.isAuthenticated"
      @update="$emit('is-modified', true)"
    />
    <model-table
      :data="devices"
      :noItemLabel="$t('compCarbonFormsDevicesMain.no-devices')"
      :module="module"
      :limit="1"
      :disabled="isSubmitted"
      :singleUpdateDisabled="false"
      :bulkOpsDisabled="false"
      :withTags="authStore.isAuthenticated"
      @edit="editItem"
      @update="$emit('is-modified', true)"
      @del="del"
      data-testid="devices-table"
    >
      <b-table-column
        :label="$t('compCarbonFormsDevicesMain.column-type')"
        field="type"
        sortable
        v-slot="props"
      >
        {{ i18nType(props.row) }}
      </b-table-column>

      <b-table-column
        :label="$t('compCarbonFormsDevicesMain.column-model')"
        field="model"
        sortable
        v-slot="props"
      >
        {{ i18nModel(props.row) }}
      </b-table-column>

      <b-table-column
        field="amount"
        sortable
        :label="$t('compCarbonFormsDevicesMain.column-quantity')"
        numeric
        v-slot="props"
      >
        {{ props.row.amount }}
      </b-table-column>
    </model-table>

    <devices-modal
      v-if="modalItem !== null"
      :displayModal="displayModal"
      :item="modalItem"
      :modeCreation="modeCreation"
      @close="closeModal"
      @validated="modalValidated"
    />

    <br />
    <nav class="level">
      <div class="level-left"></div>
      <div class="level-right">
        <div class="buttons">
          <b-button
            class="button"
            :disabled="isSubmitted"
            icon-left="plus"
            @click="addItem()"
          >
            {{ $t('add') }}
          </b-button>
          <b-button
            class="level-item"
            :icon-left="validSavedIcon"
            :disabled="!flags.isModified"
            type="is-primary"
            @click="validate"
            data-testid="devices-save"
          >
            <span v-if="authStore.isAuthenticated">{{ $t('save') }}</span>
            <span v-else>{{ $t('validate') }}</span>
          </b-button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
/* eslint-disable camelcase */

import Modules from '@/models/Modules.js'
import ComputerDevice from '@/models/carbon/ComputerDevice'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useGHGIStore } from '@/stores/ghgi.js'
import { useAdminStore } from '@/stores/admin.js'
import { setup } from '@ecodiag/ecodiag-lib'

export default {
  name: 'Main',
  components: {
    'my-notification': () => import('@/components/MyNotification.vue'),
    'serveur-info': () => import('@/components/ServeurInfo.vue'),
    'help-file-format': () =>
      import('@/components/carbon/forms/HelpFileFormat.vue'),
    'model-table': () => import('@/components/carbon/forms/ModelTable.vue'),
    'devices-modal': () =>
      import('@/components/carbon/forms/devices/Modal.vue'),
    upload: () => import('@/components/carbon/forms/Upload.vue')
  },
  $_veeValidate: {
    validator: 'new'
  },
  props: {
    flags: {
      type: Object
    }
  },
  data () {
    return {
      error: '',
      source: null,
      modalItem: null,
      modeCreation: true,
      displayModal: false
    }
  },
  setup () {
    const authStore = useAuthenticationStore()
    const adminStore = useAdminStore()
    const ghgiStore = useGHGIStore()
    return { authStore, adminStore, ghgiStore }
  },
  computed: {
    formatDescription: function () {
      return ComputerDevice.formatDescription
    },
    isSubmitted: function () {
      return this.ghgiStore.item.isSubmitted
    },
    infos: function () {
      let totalValid = this.ghgiStore.validItems(
        this.module,
        this.source
      ).length
      let total =
        this.ghgiStore.invalidItems(this.module, this.source).length +
        totalValid
      return [
        {
          label: this.$t('compCarbonFormsDevicesMain.valid-rows'),
          value: totalValid + ' / ' + total
        },
        {
          label: this.$t('compCarbonFormsDevicesMain.ncomputers'),
          value: this.ghgiStore
            .validItems(this.module, this.source)
            .map((obj) => {
              if (obj.isComputer) {
                return obj.amount
              } else {
                return 0
              }
            })
            .reduce((a, b) => parseInt(a) + parseInt(b), 0)
        },
        {
          label: this.$t('compCarbonFormsDevicesMain.nscreens'),
          value: this.ghgiStore
            .validItems(this.module, this.source)
            .map((obj) => {
              if (obj.isScreen) {
                return obj.amount
              } else {
                return 0
              }
            })
            .reduce((a, b) => parseInt(a) + parseInt(b), 0)
        }
      ]
    },
    module: function () {
      return Modules.DEVICES
    },
    validSavedIcon () {
      let icon = null
      if (this.authStore.isAuthenticated) {
        icon = 'save'
      } else {
        icon = 'check'
      }
      return icon
    },
    devices: function () {
      return this.ghgiStore.items(this.module).filter((item) => {
        if (this.source === null) {
          return true
        } else {
          return item.source === this.source
        }
      })
    },
    currentYear () {
      return this.ghgiStore.item.year
    }
  },
  mounted () {
    setup({
      damping_factor: 1,
      ignore_year: true,
      get_default_model: function (type) {
        return 'default'
      }
    })
  },
  methods: {
    i18nType (device) {
      // check if device.type is in i18n messages
      if (this.$i18n.te(device.type)) {
        return this.$i18n.t(device.type)
      }
      return device.i18nType(this.$i18n.locale)
    },
    i18nModel (device) {
      // check if device.type is in i18n messages
      if (this.$i18n.te(device.model)) {
        return this.$i18n.t(device.model)
      }
      let label = device.i18nModel(this.$i18n.locale)
      // special marker (see ecodiag-lib) to let the caller decide how it names
      // the default model
      if (label === '__default__') {
        label = this.$i18n.t('labels.default_model')
      }
      return label
    },
    callback: function (data, format, source) {
      this.error = null
      this.isLoading = true
      let allTags = this.adminStore.allTagCategories
        .map((obj) => obj.tags)
        .flat()
      let devices = ComputerDevice.createFromLines(
        data,
        format,
        this.currentYear,
        allTags,
        source
      )
      for (let device of devices) {
        this.ghgiStore.addItem({
          module: this.module,
          item: device
        })
      }
      this.isLoading = false
    },
    del: function (items) {
      this.ghgiStore.deleteItems({
        module: this.module,
        items: items
      })
    },
    editItem: function (device) {
      this.modalItem = device
      this.modeCreation = false
      this.displayModal = true
    },
    validate: function () {
      let self = this
      let invalidItems = this.devices.filter((e) => !e.isValid())
      if (invalidItems.length > 0) {
        this.$buefy.dialog.confirm({
          title: self.$t('compCarbonFormsDevicesMain.title-2'),
          message: self.$t('compCarbonFormsDevicesMain.error-msg'),
          confirmText: this.$t('validate'),
          canCancel: ['escape', 'button', 'outside'],
          onConfirm: function () {
            self.ghgiStore.deleteInvalidItems(self.module)
            self.subValidate()
          }
        })
      } else {
        this.subValidate()
      }
    },
    subValidate: function () {
      if (this.authStore.isAuthenticated) {
        const loadingComponent = this.$buefy.loading.open({
          container: null
        })
        this.ghgiStore
          .saveItems(this.module)
          .then((data) => {
            loadingComponent.close()
            this.$emit('is-valid', this.ghgiStore.items(this.module).length > 0)
            this.$emit('is-modified', false)
            this.error = ''
            window.scrollTo(0, 0)
            this.$buefy.toast.open({
              type: 'is-light',
              message: this.$t('compCarbonFormsDevicesMain.save-msg')
            })
            this.source = null
          })
          .catch((error) => {
            loadingComponent.close()
            this.$emit('is-valid', false)
            this.error = error.response.data
          })
      } else {
        this.$emit('is-valid', this.ghgiStore.items(this.module).length > 0)
        this.$emit('is-modified', false)
      }
    },
    addItem () {
      this.modalItem = ComputerDevice.createEmpty(this.currentYear)
      this.modeCreation = true
      this.displayModal = true
    },
    modalValidated (item) {
      if (this.modeCreation) {
        this.ghgiStore.addItem({
          module: this.module,
          item: item
        })
      } else {
        this.ghgiStore.updateItem({
          module: this.module,
          item: {
            from: this.modalItem,
            to: item
          }
        })
      }
      this.$emit('is-modified', true)
      this.modalItem = null
      this.displayModal = false
    },
    closeModal () {
      this.displayModal = false
      this.modalItem = null
    }
  }
}
</script>
